// A lot created offline as the device shows it until its create syncs (BL-2431, ADR 066 — Offline
// Mode § 4.6): built from the create the queue holds, keyed by its client id, Draft, with no lot or
// inventory number (the server mints both at sync). The replay drops it once the create lands.
import type { MoneyWire } from '@auctogon/core/money';
import type { LotCreateRequestPayload } from '@auctogon/core-admin/schemas/item-form';
import type { InventoryListItemDto } from '@auctogon/core-admin/schemas/responses/inventory-list';
import type { LotDetailPayload } from '@auctogon/core-admin/schemas/responses/lot-detail';
import type { OfflineReferenceDto } from '@auctogon/core-admin/schemas/responses/offline-reference';
import type { OfflineDeltaLotDto } from '@auctogon/core-admin/schemas/responses/offline-sync';

import type { LotPatchFields } from './offline-lot-edit';
import { applyLotEditOverlay } from './offline-lot-overlay';

/** The auction a lot is created in (the auction door), or null for the inventory door. */
export interface OfflineNewLotPlacement {
  auctionId: string;
  auctionName: string | null;
  auctionTimeZone: string | null;
}

const DRAFT_EDIT_LOCK: LotDetailPayload['editLock'] = {
  isSold: false,
  isActive: false,
  hasBids: false,
  isDraftOrReady: true,
  hasActivePreBids: false,
};

/** An empty Starting Price stores zero (BUG-1284), in the lot's currency. */
function startPriceOf(payload: LotCreateRequestPayload, currency: string): MoneyWire {
  return payload.startPrice ?? { amount: '0', currency };
}

function emptyDetail(input: { clientId: string; tenantId: string; currency: string; payload: LotCreateRequestPayload; at: string }): LotDetailPayload {
  const { clientId, tenantId, currency, payload, at } = input;
  return {
    id: clientId, tenantId, lotNumber: null, lotNumberSuffix: '', name: payload.name, description: '', image: null,
    phaseKind: 'draft', editLock: DRAFT_EDIT_LOCK, startPrice: startPriceOf(payload, currency), reserve: null,
    taxStatus: 'regular', taxRate: null, rep: '', salesRepId: null,
    showSalesRep: true, showSeller: false, showAdditionalContact: false,
    additionalContactTitle: null, additionalContactName: null, additionalContactPhone: null, additionalContactEmail: null,
    sellerSalesRepId: null, sellerSalesRepName: null, sellerId: '', sellerIdentity: null,
    // Its client id until it syncs (BL-2447 Q1): the drawer shows Photos and Documents for a lot with an item.
    inventoryItemId: clientId,
    lotCountForItem: 1, inventoryNumber: null, isOffSite: false, location: null, locationId: null, documents: [],
    parentLotId: null, parentLotNumberLabel: null, linkType: null, acreage: null, pricingType: null, categoryId: null,
    createdAt: at, updatedAt: at, createdBy: null, updatedBy: null, auctionId: null, auctionTimeZone: null,
    auctionName: null, auctionLocation: null, inventoryDescription: null, media: [], make: null, model: null, year: null,
    vin: null, serialNumber: null, hoursMileage: null, mileageUnit: null, condition: null, isTitledItem: false,
    titleStatus: null, titleState: null, titleMailingStatus: null, titleMailingTrackingNumber: null,
    titleMailingDateSent: null, titleMailingRecipientName: null, titleMailingRecipientAddress: null,
    hasLoadOutFee: false, loadOutFee: null, visibleToSeller: false, customFields: {},
  };
}

function emptyRow(input: { clientId: string; tenantId: string; currency: string; payload: LotCreateRequestPayload; at: string }): InventoryListItemDto {
  const { clientId, tenantId, currency, payload, at } = input;
  return {
    id: clientId, tenantId, inventoryNumber: '', name: payload.name, status: 'draft', createdAt: at, updatedAt: at,
    taxStatus: 'regular', taxRate: null, lotId: clientId, startPrice: startPriceOf(payload, currency), reserve: null,
    hammerPrice: null, hammerAdjustment: null, inventoryItemId: clientId, lotCountForItem: 1,
    moveLock: { isDraftOrReady: true, hasBids: false, hasActivePreBids: false }, lotPhaseKind: 'draft', media: [],
  };
}

/**
 * The device's record of a lot created offline: the create's fields written onto an empty Draft
 * lot through the same overlay an offline edit uses, so a field is named one way in both.
 *
 * @param input.clientId - the create's client id (its `clientOpId` and queue entry id).
 * @param input.tenantId - the house.
 * @param input.currency - the auction's currency (auction door) or the house's (inventory door).
 * @param input.payload - the create's lot fields, as the form sent them.
 * @param input.placement - the auction it is created in, or null.
 * @param input.reference - the synced picker lists, for the names the screens show.
 * @param input.at - now, ISO 8601.
 * @returns the lot record (detail, list row and no notes).
 */
export function buildOfflineNewLot(input: {
  clientId: string;
  tenantId: string;
  currency: string;
  payload: LotCreateRequestPayload;
  placement: OfflineNewLotPlacement | null;
  reference: OfflineReferenceDto;
  at: string;
}): OfflineDeltaLotDto {
  const { payload, placement } = input;
  const where = {
    auctionId: placement?.auctionId ?? null,
    auctionName: placement?.auctionName ?? null,
    auctionTimeZone: placement?.auctionTimeZone ?? null,
  };
  const base: OfflineDeltaLotDto = {
    detail: { ...emptyDetail(input), ...where },
    row: { ...emptyRow(input), auctionId: where.auctionId ?? undefined, auctionName: where.auctionName ?? undefined },
    notes: [],
  };
  // The create's fields, minus what is not a lot field (staged files and notes ride the create itself).
  const fields: LotPatchFields = { ...payload };
  for (const key of ['stagedMedia', 'stagedDocuments', 'internalNotes', 'clientOpId']) delete fields[key];
  // An empty Starting Price is the zero the base already holds, never a null price.
  const changes = fields.startPrice == null ? { ...fields, startPrice: base.detail.startPrice } : fields;
  return applyLotEditOverlay({ lot: base, changes, reference: input.reference });
}
